/**
 * Video Generator Service
 * Generates FIFA-style team reveal videos using Canvas + MediaRecorder API
 */

import { TeamPlayer } from '../types';
import { apiClient } from '../api/client';

export interface VideoGeneratorOptions {
  teamA: TeamPlayer[];
  teamB: TeamPlayer[];
  bibsTeam: 'A' | 'B';
  onProgress?: (progress: number, status: string) => void;
}

interface PlayerCardData {
  player: TeamPlayer;
  imageData: string | null;
  hasCard: boolean;
}

export class VideoGenerator {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width = 1080;
  private height = 1920;
  private fps = 30;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.ctx = this.canvas.getContext('2d')!;
  }

  /**
   * Generate team reveal video
   */
  async generateVideo(options: VideoGeneratorOptions): Promise<Blob> {
    const { teamA, teamB, bibsTeam, onProgress } = options;

    // Load all player card DATA from API
    onProgress?.(5, 'Loading player cards...');
    const teamACards = await this.loadPlayerCards(teamA);
    const teamBCards = await this.loadPlayerCards(teamB);

    // Pre-load ALL card images as HTMLImageElement objects BEFORE recording
    onProgress?.(8, 'Pre-loading card images...');
    const loadedImages = new Map<string, HTMLImageElement>();
    for (const card of [...teamACards, ...teamBCards]) {
      if (card.imageData) {
        const img = await this.loadImage(card.imageData);
        loadedImages.set(card.player.player_id, img);
      }
    }

    // Setup MediaRecorder
    onProgress?.(10, 'Setting up video recorder...');
    const stream = this.canvas.captureStream(this.fps);
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 5000000, // 5 Mbps
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    try {
      // Start recording RIGHT before rendering (after ALL images are loaded)
      mediaRecorder.start();

      // Render video frames (pass pre-loaded images)
      await this.renderVideoFrames(teamACards, teamBCards, bibsTeam, loadedImages, onProgress);

      // Stop recording
      mediaRecorder.stop();

      // Wait for final data
      await new Promise((resolve) => {
        mediaRecorder.onstop = resolve;
      });

      onProgress?.(95, 'Finalizing video...');

      // Combine chunks into final blob
      const webmBlob = new Blob(chunks, { type: 'video/webm' });

      onProgress?.(100, 'Video complete!');
      return webmBlob;
    } catch (error) {
      mediaRecorder.stop();
      throw error;
    }
  }

  /**
   * Load player card images from API
   */
  private async loadPlayerCards(players: TeamPlayer[]): Promise<PlayerCardData[]> {
    const cardPromises = players.map(async (player) => {
      try {
        const data = await apiClient.getPlayerCard(player.player_id);

        if (data.success && data.has_card && data.image) {
          return { player, imageData: data.image, hasCard: true };
        }
        return { player, imageData: null, hasCard: false };
      } catch (error) {
        console.error(`Failed to load card for ${player.name}:`, error);
        return { player, imageData: null, hasCard: false };
      }
    });

    return Promise.all(cardPromises);
  }

  /**
   * Render all video frames
   */
  private async renderVideoFrames(
    teamACards: PlayerCardData[],
    teamBCards: PlayerCardData[],
    bibsTeam: 'A' | 'B',
    loadedImages: Map<string, HTMLImageElement>,
    onProgress?: (progress: number, status: string) => void
  ): Promise<void> {
    const totalPlayers = teamACards.length + teamBCards.length;
    let currentPlayer = 0;

    // Scene 1: Team A Header (2 seconds)
    onProgress?.(15, 'Rendering Team A header...');
    await this.renderTeamHeader('TEAM A', bibsTeam === 'A', 2);

    // Scene 2: Team A Players (2 seconds each for smooth transitions)
    for (const card of teamACards) {
      currentPlayer++;
      const progress = 15 + ((currentPlayer / totalPlayers) * 40);
      onProgress?.(progress, `Revealing ${card.player.name}...`);
      const preloadedImage = loadedImages.get(card.player.player_id) || null;
      await this.renderPlayerCardWithImage(card, preloadedImage, 2.0);
    }

    // Scene 3: Team B Header (2 seconds)
    onProgress?.(60, 'Rendering Team B header...');
    await this.renderTeamHeader('TEAM B', bibsTeam === 'B', 2);

    // Scene 4: Team B Players (2 seconds each for smooth transitions)
    for (const card of teamBCards) {
      currentPlayer++;
      const progress = 60 + ((currentPlayer / totalPlayers) * 25);
      onProgress?.(progress, `Revealing ${card.player.name}...`);
      const preloadedImage = loadedImages.get(card.player.player_id) || null;
      await this.renderPlayerCardWithImage(card, preloadedImage, 2.0);
    }

    // Scene 5: Final Summary with Cards (5 seconds)
    onProgress?.(90, 'Rendering final summary...');
    await this.renderFinalSummaryWithCards(teamACards, teamBCards, bibsTeam, loadedImages, 5);
  }

  /**
   * Render team header scene
   */
  private async renderTeamHeader(
    teamName: string,
    hasBibs: boolean,
    durationSeconds: number
  ): Promise<void> {
    const frames = Math.floor(durationSeconds * this.fps);

    for (let i = 0; i < frames; i++) {
      // Clear canvas with gradient background
      const gradient = this.ctx.createLinearGradient(0, 0, 0, this.height);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, this.width, this.height);

      // Team name
      this.ctx.fillStyle = '#ffffff';
      this.ctx.font = 'bold 120px Arial';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(teamName, this.width / 2, this.height / 2 - 50);

      // Bibs subtitle
      if (hasBibs) {
        this.ctx.fillStyle = '#ffd700';
        this.ctx.font = 'bold 60px Arial';
        this.ctx.fillText('(Wearing Bibs)', this.width / 2, this.height / 2 + 50);
      }

      await this.waitFrame();
    }
  }

  /**
   * Render individual player card with pre-loaded image and slide transitions
   */
  private async renderPlayerCardWithImage(
    card: PlayerCardData,
    preloadedImage: HTMLImageElement | null,
    durationSeconds: number
  ): Promise<void> {
    const totalFrames = Math.floor(durationSeconds * this.fps);
    const transitionFrames = Math.floor(this.fps * 0.25); // 0.25 second transitions (faster, smoother)
    const holdFrames = totalFrames - (2 * transitionFrames); // Hold in center

    let frameIndex = 0;

    // Render all frames with consistent timing
    for (let i = 0; i < totalFrames; i++) {
      // Clear canvas with gradient background
      const gradient = this.ctx.createLinearGradient(0, 0, 0, this.height);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, this.width, this.height);

      // Calculate x position for slide animation
      let xOffset = 0;

      if (i < transitionFrames) {
        // Slide in from left
        const progress = i / transitionFrames;
        const eased = this.easeOutCubic(progress);
        xOffset = -this.width * (1 - eased);
      } else if (i >= transitionFrames + holdFrames) {
        // Slide out to right
        const progress = (i - transitionFrames - holdFrames) / transitionFrames;
        const eased = this.easeInCubic(progress);
        xOffset = this.width * eased;
      }
      // else: xOffset stays 0 (card in center)

      if (preloadedImage) {
        // Draw pre-loaded uploaded FIFA card with offset
        this.drawLoadedCardImageWithOffset(preloadedImage, xOffset);
      } else {
        // Draw placeholder card with offset
        this.drawPlaceholderCardWithOffset(card.player, xOffset);
      }

      await this.waitFrame();
      frameIndex++;
    }
  }

  /**
   * Easing function for smooth slide in
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Easing function for smooth slide out
   */
  private easeInCubic(t: number): number {
    return t * t * t;
  }

  /**
   * Load image from Base64 data
   */
  private async loadImage(imageData: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageData;
    });
  }

  /**
   * Draw pre-loaded card image with horizontal offset for slide animation
   */
  private drawLoadedCardImageWithOffset(img: HTMLImageElement, xOffset: number): void {
    // Center the card image
    const maxWidth = this.width * 0.8;
    const maxHeight = this.height * 0.7;
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
    const drawWidth = img.width * scale;
    const drawHeight = img.height * scale;
    const x = (this.width - drawWidth) / 2 + xOffset;
    const y = (this.height - drawHeight) / 2;

    this.ctx.drawImage(img, x, y, drawWidth, drawHeight);
  }

  /**
   * Draw placeholder card with horizontal offset for slide animation
   */
  private drawPlaceholderCardWithOffset(player: TeamPlayer, xOffset: number): void {
    const cardWidth = 600;
    const cardHeight = 900;
    const x = (this.width - cardWidth) / 2 + xOffset;
    const y = (this.height - cardHeight) / 2;

    // Card background
    this.ctx.fillStyle = '#2d3748';
    this.ctx.fillRect(x, y, cardWidth, cardHeight);

    // Card border
    this.ctx.strokeStyle = '#ffd700';
    this.ctx.lineWidth = 8;
    this.ctx.strokeRect(x, y, cardWidth, cardHeight);

    // Player name only
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 60px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText(player.name, x + cardWidth / 2, y + cardHeight / 2);
  }

  /**
   * Render final summary scene with player cards
   */
  private async renderFinalSummaryWithCards(
    teamACards: PlayerCardData[],
    teamBCards: PlayerCardData[],
    bibsTeam: 'A' | 'B',
    loadedImages: Map<string, HTMLImageElement>,
    durationSeconds: number
  ): Promise<void> {
    const frames = Math.floor(durationSeconds * this.fps);
    const maxTeamSize = Math.max(teamACards.length, teamBCards.length);

    // Calculate card dimensions for single column vertical layout
    const headerHeight = 180;
    const availableHeight = this.height - headerHeight - 80;
    const sideWidth = this.width / 2 - 40;

    // Single column layout
    const cardWidth = sideWidth - 40;
    const spacing = 15;
    const cardHeight = Math.min(cardWidth * 1.4, (availableHeight - (maxTeamSize - 1) * spacing) / maxTeamSize);

    for (let i = 0; i < frames; i++) {
      // Clear canvas with gradient background
      const gradient = this.ctx.createLinearGradient(0, 0, 0, this.height);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, this.width, this.height);

      // Team A side (left) - Blue accent
      this.renderTeamSideVertical(teamACards, loadedImages, bibsTeam === 'A', 'TEAM A', '#3b82f6', 20, headerHeight, sideWidth, cardWidth, cardHeight, spacing);

      // Team B side (right) - Red accent
      this.renderTeamSideVertical(teamBCards, loadedImages, bibsTeam === 'B', 'TEAM B', '#ef4444', this.width / 2 + 20, headerHeight, sideWidth, cardWidth, cardHeight, spacing);

      await this.waitFrame();
    }
  }

  /**
   * Render one team's side in the final summary (vertical single column)
   */
  private renderTeamSideVertical(
    cards: PlayerCardData[],
    loadedImages: Map<string, HTMLImageElement>,
    hasBibs: boolean,
    teamName: string,
    accentColor: string,
    startX: number,
    startY: number,
    sideWidth: number,
    cardWidth: number,
    cardHeight: number,
    spacing: number
  ): void {
    // Team header with colored background
    this.ctx.fillStyle = accentColor;
    this.ctx.fillRect(startX, 40, sideWidth, 100);

    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 50px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText(teamName, startX + sideWidth / 2, 100);

    if (hasBibs) {
      this.ctx.fillStyle = '#ffd700';
      this.ctx.font = 'bold 28px Arial';
      this.ctx.fillText('(BIBS)', startX + sideWidth / 2, 130);
    }

    // Render player cards in vertical single column
    cards.forEach((card, index) => {
      const x = startX + 20;
      const y = startY + 20 + index * (cardHeight + spacing);

      const preloadedImage = loadedImages.get(card.player.player_id);

      if (preloadedImage) {
        // Draw uploaded card (scaled to fit)
        this.drawScaledCard(preloadedImage, x, y, cardWidth, cardHeight);
      } else {
        // Draw mini placeholder card
        this.drawMiniPlaceholderCard(card.player, x, y, cardWidth, cardHeight);
      }
    });
  }

  /**
   * Draw scaled card image to fit specific dimensions
   */
  private drawScaledCard(img: HTMLImageElement, x: number, y: number, width: number, height: number): void {
    const scale = Math.min(width / img.width, height / img.height);
    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;
    const offsetX = (width - scaledWidth) / 2;
    const offsetY = (height - scaledHeight) / 2;

    this.ctx.drawImage(img, x + offsetX, y + offsetY, scaledWidth, scaledHeight);
  }

  /**
   * Draw mini placeholder card for final summary
   */
  private drawMiniPlaceholderCard(player: TeamPlayer, x: number, y: number, width: number, height: number): void {
    // Card background
    this.ctx.fillStyle = '#2d3748';
    this.ctx.fillRect(x, y, width, height);

    // Card border
    this.ctx.strokeStyle = '#ffd700';
    this.ctx.lineWidth = 3;
    this.ctx.strokeRect(x, y, width, height);

    // Player name (scaled font)
    this.ctx.fillStyle = '#ffffff';
    const fontSize = Math.min(width / 8, 24);
    this.ctx.font = `bold ${fontSize}px Arial`;
    this.ctx.textAlign = 'center';
    this.ctx.fillText(player.name, x + width / 2, y + height / 2, width - 10);
  }

  /**
   * Wait for one frame duration
   */
  private waitFrame(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, 1000 / this.fps);
    });
  }

  /**
   * Download blob as file
   */
  static downloadVideo(blob: Blob, filename: string = 'team-reveal.webm'): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
